/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'./DataPlayerEntryCreator',
	'../../../widgets/livewidget/nls/StringResources'
], function(
	$,
	DataPlayerEntryCreator,
	StringResources
) {
	const TICK_MIN_DISTANCE = 44;
	const BAR_OVERFLOW = 60;
	const VERTICAL_POS_SCALE_FACTOR = 2/3;
	const SNAP_THRESHOLD = 15;
	const MAX_LABEL_LAYER_COUNT = 2;

	class DataPlayerSlider {

		constructor() {
			// main list of player entries
			this.playerEntries = [];
		}

		// Private methods

		/**
		* @description Initialize the node that contains the slider
		* @param {Element} $sliderContainerNode - JQuery node that contains the slider
		*/
		_initSliderContainer($sliderContainerNode) {
			this.$_sliderContainer = $sliderContainerNode;
			this.$_sliderContainer.attr('aria-label', StringResources.get('dataPlayerValueListLabel'));
			this.$_sliderContainer.empty();
		}

		/**
		* @description Creates and adds the tick decoration element
		*/
		_createTickDecoration() {
			this.$_tickDecorationNode = $('<div class="sliderNode"><svg class="sliderSymbol" height="18" width="18"><circle cx="10" cy="10" r="8" fill-opacity="1"/></div>');
			this.$_sliderContainer.append(this.$_tickDecorationNode);
		}

		/**
		* @description Create the slider bar
		* @param {Double} barTop - top position of the bar
		* @param {Double} barLeft - left position of the bar
		* @param {Double} barLength - length of the bar
		*/
		_createSliderBar(barTop, barLeft, barLength) {
			this.$_sliderBar = $('<div class="sliderBarHorizontal"></div>');
			this.$_sliderContainer.append(this.$_sliderBar);
			this.$_sliderBar.css('top', barTop + 'px');
			this.$_sliderBar.css('left', barLeft + 'px');
			this.$_sliderBar.css('width', barLength + 'px');
		}

		/**
		* This is a helper function to position the labels passed in as an array.
		* The array of labels contains initial position information.  Labels are
		* recursively processed and layered so that they do not collide with each other.
		* If a label does not fit, then it is moved to a new layer above the other labels.
		*	@param {Array} aoLabels list of label objects inclusing positon info
		**/
		/* Sorry, I'm not touching this until the next version as I may just carbonize it*/
		_positionLabels(aoLabels) {
			// check for empty
			if (this.playerEntries.length === 0) {
				return;
			}

			// create the label layers array (labels can be layered for collision avoidance)
			let aaLabelLayers = [aoLabels];

			// loop and process the layers array until all layers have been processed
			// layers will get added as collisions are detected
			for (let i = 0; i < aaLabelLayers.length; i++) {

				// process the last layer in the array
				this._processLabelLayers(aaLabelLayers);

				// now that the layer has been processed, positon the labels in it
				for (let j = 0; j < aaLabelLayers[i].length; j++) {

					let oLabel = aaLabelLayers[i][j];
					oLabel.$el.css('top', Math.max(-8, (oLabel.labelBaseline - (oLabel.height * (i + 1)))) + 'px');
					oLabel.$el.css('left', oLabel.left + 'px');
				}

				// if the layer count exceeds the max then rotate the labels
				if (aaLabelLayers.length > MAX_LABEL_LAYER_COUNT) {

					// collapse all labels into a single array
					let aoRotateLabels = [];
					for (let k = 0; k < aaLabelLayers.length; k++) {
						$.merge(aoRotateLabels, aaLabelLayers[k]);
					}

					// rotate labels
					this._rotateLabels(aoRotateLabels);

					// no need to continue processing layers
					break;
				}
			}
		}

		/**
		* This is a helper function that processes a single layer of labels which is
		* taken from the first elemenrt of the array passed in.  If collisions are
		* detected between elements of this array then those elements are pushed into
		* a new array list that is then appended to the passed in array for further
		* processing.
		*	@param {Array} aaLabelLayers array of arrays to be processed.  Only the
		* first element of the array is processed.
		**/
		/* Sorry, I'm not touching this until the next version as I may just carbonize it*/
		_processLabelLayers(aaLabelLayers) {
			// init next layer if needed
			let aNewLabelLayer = [];
			let iPrevLabelEnd = -99999999;

			// process the last layer in the array
			let aoLabelLayer = aaLabelLayers[aaLabelLayers.length - 1];
			for (let i = 0; i < aoLabelLayer.length; i++) {

				// does this label's left collide with the previous end
				if (aoLabelLayer[i].left < iPrevLabelEnd) {

					// move this label to the next layer
					aNewLabelLayer = aNewLabelLayer.concat(aoLabelLayer.splice(i, 1));

					// array was altered so next element has replaced this one.
					i--;

				} else {

					// just record the ending point and move along
					iPrevLabelEnd = aoLabelLayer[i].right;
				}
			}

			// check if any labels were added to the new array and append as necessary
			if (aNewLabelLayer.length > 0) {
				aaLabelLayers.push(aNewLabelLayer);
			}
		}

		/**
		* This is a helper function that takes an array of labels and positions and rotates them.
		* @param {Array} aoRotateLabels list of labels to rotate and position
		**/
		/* Sorry, I'm not touching this until the next version as I may just carbonize it*/
		_rotateLabels(aoRotateLabels) {
			if (aoRotateLabels.length < 1) {
				return;
			}

			const widgetWidget = this._getWidgetWidth();

			// The top is the same for all the labels, so calculate it once
			const top = Math.max(-8, (aoRotateLabels[0].labelBaseline - aoRotateLabels[0].height));

			// Since the labels are shown at a 45 degree angle, use Pythagorean theorem to calculate the maximum length
			const maxWidth = Math.max(25, Math.floor(Math.sqrt(top * top + top * top)));

			// loop and process all labels
			for (let i = 0; i < aoRotateLabels.length; i++) {

				// position the label and rotate (restore width for possoibly truncated labels)
				let $oLabel = aoRotateLabels[i].$el;
				$oLabel.css('top', top + 'px');
				$oLabel.css('left', aoRotateLabels[i].tickLeft + 'px');

				// If ever we don't have enough room left over to do a 45 degree triangle (top), then recalculate the max width.  This hanppens
				// at the end of the data player, the last few labels would extend past the right edge and cause scrollbars
				if ((widgetWidget - aoRotateLabels[i].tickLeft) < top) {
					const roomLeft = widgetWidget - aoRotateLabels[i].tickLeft;
					$oLabel.css('width', Math.floor(Math.sqrt(roomLeft * roomLeft + roomLeft * roomLeft)) + 'px');
				} else {
					$oLabel.css('width', maxWidth + 'px');
				}
				$oLabel.addClass('rotated');
			}
		}

		/**
		* @description Calculate the position for the tick of the given data index
		*	@param {Integer} dataItemIndex index of the data item to use
		*	@return {Object} top and left position of the slider
		*/
		_calcTickPosition(dataItemIndex) {
			let tickPosition = {};

			let entry = this.playerEntries[dataItemIndex];
			if (entry) {
				tickPosition = this._getTickTopLeftPosition(entry);
			}

			return tickPosition;
		}

		/**
		* @description Calculate the Top and left for the tick of the given data index
		*	@param {Object} playerEntry description
		*	@return {Object} top and left position of the slider
		*/
		_getTickTopLeftPosition(entry){
			let position = {};
			if (entry && entry.tick) {
				// set slider top (need slight adjustment for visual alignment of decoration)
				position.top = Math.round(entry.tick.top + entry.tick.height / 2 - this._getTickDecorationSize().height / 2);

				// set slider left
				position.left = Math.round(entry.tick.left + entry.tick.width / 2 - this._getTickDecorationSize().width / 2);
			}

			return position;
		}

		_animateToScrollLeftPosition(position) {
			this.$_sliderContainer.animate({
				scrollLeft: position - BAR_OVERFLOW
			});
		}

		_getWidgetWidth() {
			return this.$_sliderContainer[0].scrollWidth || this.$_sliderContainer[0].clientWidth;
		}

		/**
		* @description The decoration is always the same size, so cache it and return width/height
		*	@return {Object} width and height of the decoration node. Normally cached.
		*/
		_getTickDecorationSize() {
			if (!this.tickDecorationSize) {
				this.tickDecorationSize = {
					width:this.$_tickDecorationNode[0].clientWidth,
					height: this.$_tickDecorationNode[0].clientHeight
				};
			}
			return this.tickDecorationSize;
		}

		/**
		* @description Move tick decoration to the correct position
		*	@param {Double} leftPosition left most edge for the tick decoration
		*	@param {Boolean} shouldAnimate true iff the decoration animate to its new position
		*/
		_moveTickDecorationToPosition(leftPosition, shouldAnimate) {
			const styles = {
				left: leftPosition
			};
			// now move to new position
			if (shouldAnimate) {
				this.$_tickDecorationNode.animate(styles, 200);
			} else {
				this.$_tickDecorationNode.css(styles);
			}
		}

		// Public methods
		/**
		* @description Hide the tick decoration
		*/
		hideTickDecoration() {
			this.$_tickDecorationNode.css('opacity', 0);
			this.$_tickDecorationNode.css('display', 'none');
		}

		/**
		* @description Show the tick decoration
		*/
		showTickDecoration() {
			this.$_tickDecorationNode.css({
				opacity: 1,
				display: 'block'
			});
		}

		/**
		* @description Move the tick decoration to a left position in pixels
		*	@param {Double} posInPx left placement in pixels
		*/
		setTickDecorationLeftPosition(posInPx) {
			this.$_tickDecorationNode.css('left', posInPx + 'px');
		}

		/**
		* @description Move the tick decoration to a top position in pixels
		*	@param {Double} posInPx top placement in pixels
		*/
		setTickDecorationTopPosition(posInPx) {
			this.$_tickDecorationNode.css('top', posInPx + 'px');
		}

		/**
		* @description moves the slider to the tick for the corresponding passed in
		* data index.  It can optionally be animated and made visible. This function
		* also sets the play index counter to the next tick down the line.
		*	@param {Integer} dataItemIndex index of the playerEntry object
		*	@param {Boolean} animate true iff we should animate the movement
		*	@param {Boolean} showDecoration true iff the decoration should be shown
		* @returns {Integer} next index to play to (may wrap back to beginning)
		*/
		positionSlider(dataItemIndex, animate, showDecoration) {
			// ensure the node is visible
			if (showDecoration) {
				this.showTickDecoration();
			}

			// get scrollLeftPosition and container width
			const scrollLeftPos = this.getScrollLeftPosition();
			const sliderViewPortWidth = this.getSliderSize().width;

			// get slider position
			const oSliderPosition = this._calcTickPosition(dataItemIndex);

			// set top
			this.setTickDecorationTopPosition(oSliderPosition.top);

			// scroll the div back if the node goes off the front or back of scroll
			if ((oSliderPosition.left >= scrollLeftPos + sliderViewPortWidth - 10) ||
					(oSliderPosition.left < scrollLeftPos)) {
				this._animateToScrollLeftPosition(oSliderPosition.left);
			}

			this._moveTickDecorationToPosition(oSliderPosition.left, animate);

			// Return the next playindex
			return ((dataItemIndex + 1) % this.playerEntries.length);
		}

		/**
		* @description get the value property of the playerEntry at the specified index
		* @returns {String} value of the playerEntry if one exist, undefined otherwise
		*/
		getValueAtIndex(index) {
			return this.playerEntries[index] && this.playerEntries[index].value;
		}

		/**
		* @description determine if there are any player entries defined
		* @returns {Boolean} true iff there are playerEntry's for the dataplayer
		*/
		hasPlayerEntries() {
			return this.playerEntries && this.playerEntries.length;
		}

		/**
		* @description get the playerEntry with the specified value
		*	@param {String} dataItemValue value of the data entry to look up
		* @returns {Object} playerEntry if one matches the value provided, undefined otherwise
		*/
		getPlayerEntryWithValue(dataItemValue) {
			return this.playerEntries.find(entry => {return entry.value.value === dataItemValue;});
		}

		/**
		* @description Determine the tick closest tick to snap to.
		*	@param {Double} leftPosition the left position of the slider tick decoration.
		*	@param {Boolean} splitThreshold is the threshold pre-defined or split between ticks.
		* @returns {Object} left position and index of the tick to snap to
		*/
		determineTickToSnapTo(leftPosition, splitThreshold) {
			// init
			const centeredLeftPos = leftPosition + this._getTickDecorationSize().width / 2;
			let newLeft = leftPosition;
			let index = -1;

			// threshold is either pre-defined or is split halfway between ticks
			let threshold = splitThreshold ? null : SNAP_THRESHOLD;

			// loop through collect and determine either snap to tick, limit to start, or limit to end
			for (let playerEntryIndex = 0; playerEntryIndex < this.playerEntries.length; playerEntryIndex++) {
				const playerEntry = this.playerEntries[playerEntryIndex];

				// set threshold as necessary
				if (!threshold) {
					threshold = playerEntry.tick.spacing / 2;
				}

				// get tick center
				const tickCenterLeft = playerEntry.tick.left + playerEntry.tick.width / 2;

				// get snap left and snap right
				const snapLeft = centeredLeftPos <= tickCenterLeft + threshold;
				const snapRight = centeredLeftPos >= tickCenterLeft - threshold;

				// check if first or last entry and if snap is needed
				const isFirstAndSnap = (playerEntryIndex === 0 && snapLeft);
				const isLastAndSnap = (playerEntryIndex === this.playerEntries.length - 1) && snapRight;

				// check limits and snap to tick
				if (isFirstAndSnap || isLastAndSnap || (snapLeft && snapRight)) {

					// calculate new left & record value
					newLeft = this._calcTickPosition(playerEntryIndex).left;
					index = playerEntryIndex;
					break;
				}
			}

			return {
				left: Math.round(newLeft),
				index: index
			};
		}

		/**
		* @description Render the bar and determine the tick spacing needed
		*	@param {Array} dataItems the data items to display in the dataplayer
		*	@param {Double} barTop top position of the bar
		* @returns {Double} spacing between ticks
		*/
		_renderBarAndGetTickSpacing(dataItems, barTop) {
			let barMinLength;
			let barLength;
			let tickSpacing;

			/*
				Sanity check for data
				If there is one item, render it but disable the play button
				If there is more then one item, render as best we can.
			*/
			if (!(dataItems && dataItems.length > 1)) {
				// bar length includes overflow front and end
				barLength = this.getSliderSize().width - BAR_OVERFLOW * 2;

				// calculate the actual tick spacing
				tickSpacing = barLength / 2;
			} else {
				// calculate min length
				barMinLength = TICK_MIN_DISTANCE * (dataItems.length - 1);

				// bar length includes overflow front and end
				barLength = Math.max(barMinLength, (this.getSliderSize().width - BAR_OVERFLOW * 2));

				// calculate the actual tick spacing
				tickSpacing = barLength / (dataItems.length - 1);
			}

			this._createSliderBar(barTop, BAR_OVERFLOW/2, barLength);
			this._createTickDecoration();

			return tickSpacing;
		}

		/**
		* @description Create the player entry objects and render the labels
		*	@param {Object} dataItemAPI tAPI class that is used to work with data items.
		*	@param {Array} dataItems the data items to display in the dataplayer
		*	@param {Double} barTop top position of the bar
		* @param {Double} tickSpacing spacing between ticks
		*/
		_createPlayerEntriesAndRenderLabels(dataItemAPI, dataItems, barTop, tickSpacing) {
			const dataPlayerEntryCreator = new DataPlayerEntryCreator(this.$_tickDecorationNode, BAR_OVERFLOW, dataItemAPI);
			const aoLabels = [];

			this.playerEntries.length = 0;
			if (dataItems && dataItems.length === 1) {
				const playerEntry = dataPlayerEntryCreator.createSoleDataPlayerEntry(dataItems[0], barTop, tickSpacing);
				this.playerEntries.push(playerEntry);
				aoLabels.push(playerEntry.label);
			} else {
				for (let i = 0; i < dataItems.length; i++) {
					const isFirstOrLastItem = (i === 0 || i === dataItems.length - 1);
					const playerEntry = dataPlayerEntryCreator.createDataPlayerEntry(dataItems[i], i, barTop, tickSpacing, isFirstOrLastItem);
					this.playerEntries.push(playerEntry);
					aoLabels.push(playerEntry.label);
				}
			}

			// reset scrollleft to the start.  Some browsers seem to remember this setting and will scroll
			// to the place last left which is not necessarily correct
			this._animateToScrollLeftPosition(0);

			// position labels (collision avoidance)
			this._positionLabels(aoLabels);

		}

		/**
		* @description Render the slider
		*	@param {Object} dataItemAPI tAPI class that is used to work with data items.
		*	@param {Array} dataItems the data items to display in the dataplayer
		*	@param {Element} $sliderContainerNode JQuery element that will contain the slider
		*/
		render(dataItemAPI, dataItems, $sliderContainerNode) {
			this._initSliderContainer($sliderContainerNode);
			this.sliderSize = null;
			this.tickDecorationSize = null;

			// position from top
			const barTop = Math.round(this.getSliderSize().height * VERTICAL_POS_SCALE_FACTOR);
			const tickSpacing = this._renderBarAndGetTickSpacing(dataItems, barTop);

			this._createPlayerEntriesAndRenderLabels(dataItemAPI, dataItems, barTop, tickSpacing);

		}

		/**
		* @description Determine the width and height of the slider
		* @returns {Object} width and height of the slider container. This may be cached
		*/
		getSliderSize() {
			if (!this.sliderSize) {
				this.sliderSize = {
					width:this.$_sliderContainer[0].clientWidth,
					height: this.$_sliderContainer[0].clientHeight
				};
			}
			return this.sliderSize;
		}

		/**
		* @description Determine the scroll left position of the slider
		* @returns {Double} the scroll left position of the slider
		*/
		getScrollLeftPosition() {
			return this.$_sliderContainer.scrollLeft();
		}

		/**
		* @returns {Element} JQuery element that contains the slider
		*/
		getSliderContainer() {
			return this.$_sliderContainer;
		}

		/**
		* @description Determine the position of the tick decoration
		* @returns {Object} the position of the JQuery element
		*/
		getTickDecorationPosition() {
			return this.$_tickDecorationNode.position();
		}

	}

	return DataPlayerSlider;
});
